/**
 * useDeckMatches Hook
 * Orders the loaded upcoming fixtures into the "one at a time" deck and selects the
 * current card by index (specs/challenge-flow-redesign — Story 2).
 * "Recommended" == hot leagues first (the same league order as the "All games" list),
 * then kickoff order within a league (no ML).
 */

import { useMemo } from 'react';
import type { Game } from '../types/create.types';
import { isGameNotStarted } from '../helpers/createGames.helper';
import { LEAGUE_DISPLAY_PRIORITY } from '../constants/create.constants';

const SENTINEL_DISPLAY_ORDER = Number.MAX_SAFE_INTEGER;
/** Leagues not in LEAGUE_DISPLAY_PRIORITY sort last (after all prioritized leagues). */
const PRIORITY_INDEX_LAST = 9999;

function getLeaguePriorityIndex(leagueName: string): number {
  const i = LEAGUE_DISPLAY_PRIORITY.indexOf(leagueName.trim().toLowerCase());
  return i === -1 ? PRIORITY_INDEX_LAST : i;
}

function kickoffMs(g: Game): number {
  const t = new Date(g.rawDate ?? g.date ?? g.time).getTime();
  return Number.isNaN(t) ? Number.MAX_SAFE_INTEGER : t;
}

/**
 * Pure: pickable upcoming fixtures (not started, has real odds), hot leagues first.
 * League order mirrors the "All games" list comparator (CreateGamesStep, minus favorites):
 * API displayOrder → LEAGUE_DISPLAY_PRIORITY → league name; kickoff ascending within a league.
 * Keeps the capped deck leading with World Cup / top-flight matches instead of whatever
 * obscure fixture happens to kick off next.
 */
export const orderDeckGames = (games: Game[]): Game[] => {
  const pickable = games.filter((g) => isGameNotStarted(g) && g.hasRealOdds !== false);
  return [...pickable].sort((a, b) => {
    const orderA = a.leagueDisplayOrder ?? SENTINEL_DISPLAY_ORDER;
    const orderB = b.leagueDisplayOrder ?? SENTINEL_DISPLAY_ORDER;
    if (orderA !== orderB) return orderA - orderB;
    const priorityA = getLeaguePriorityIndex(a.league);
    const priorityB = getLeaguePriorityIndex(b.league);
    if (priorityA !== priorityB) return priorityA - priorityB;
    const byName = a.league.localeCompare(b.league, undefined, { sensitivity: 'base' });
    if (byName !== 0) return byName;
    return kickoffMs(a) - kickoffMs(b);
  });
};

/**
 * Pure: the curated deck — ordered pickable fixtures capped to `deckSize` (the user's pick
 * allowance, MAX_GAMES_PER_CHALLENGE / _PRO). A missing/non-positive cap means no cap.
 */
export const selectDeckGames = (games: Game[], deckSize?: number): Game[] => {
  const ordered = orderDeckGames(games);
  return deckSize != null && deckSize > 0 ? ordered.slice(0, deckSize) : ordered;
};

export interface UseDeckMatchesReturn {
  deckGames: Game[];
  count: number;
  /** Current card, or null when the deck is empty or the index is past the end. */
  current: Game | null;
  /** Index clamped into [0, count]. `count` means "past the end" (end state). */
  clampedIdx: number;
  isEnd: boolean;
  isEmpty: boolean;
}

export const useDeckMatches = (
  games: Game[],
  deckIdx: number,
  /**
   * Cap the curated deck to at most this many matches (defaults to the user's pick allowance —
   * MAX_GAMES_PER_CHALLENGE / _PRO). Keeps the deck a short, low-friction slate instead of a wall
   * of every fixture; the "All games" list remains the escape hatch for the full schedule.
   */
  deckSize?: number
): UseDeckMatchesReturn => {
  const deckGames = useMemo(() => selectDeckGames(games, deckSize), [games, deckSize]);
  const count = deckGames.length;
  const clampedIdx = Math.min(Math.max(0, deckIdx), count);
  const isEnd = count > 0 && clampedIdx >= count;
  return {
    deckGames,
    count,
    current: !isEnd && count > 0 ? deckGames[clampedIdx] ?? null : null,
    clampedIdx,
    isEnd,
    isEmpty: count === 0,
  };
};
